<template>
  <div
    :class="[
      'home-balance-summary',
      { 'home-balance-summary--loaded': addLoadedClass }
    ]"
    data-testid="home-balance-summary"
  >
    <SectionError
      v-if="hasError"
      class="home-balance-container-error"
      title="Não foi possível carregar o patrimônio neste momento"
      description="Aguarde alguns instantes e tente novamente."
      @on-retry="onRetry"
    />
    <HomeBalanceSummaryLoader v-if="loading" class="expand-clickable-area" />
    <div class="home-balance-container">
      <HeadingDisplay
        v-if="canLoadComponents"
        class="home-balance-summary__total"
        head="Patrimônio total"
        title=""
        :subtitle="totalUpdatedAt"
      >
        <template #title-component>
          <Money>{{ total }}</Money>
        </template>
      </HeadingDisplay>
      <HomeHero class="home-view__cash" :is-overdue="isOverdue" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue';
import { HeadingDisplay } from '@warrenbrasil/nebraska-web';
import { Money } from '@/modules/common/components/Money';

import { HomeBalanceSummaryLoader } from './components/HomeBalanceSummaryLoader';
import { SectionError } from '@/modules/common/components/SectionError';
import { TotalBalanceResponse } from '@/modules/home/services/bff';
import { HomeHero } from '../HomeHero';

interface Props {
  balanceDetails?: TotalBalanceResponse;
  loading: boolean;
  hasError: boolean;
  isOverdue: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'on-retry'): void;
}>();

const addLoadedClass = inject<boolean>('shouldTriggerHomeAnimations', false);

const retry = ref(false);
const hasHomeCashError = ref(false);

const onRetry = () => {
  retry.value = !retry.value;
  hasHomeCashError.value = false;
  emit('on-retry');
};

const total = computed(() => {
  if (!props.balanceDetails) return '';
  return (props.balanceDetails as TotalBalanceResponse).totalBalance.formatted;
});

const totalUpdatedAt = computed(() => {
  if (!props.balanceDetails) return '';
  return props.balanceDetails.updatedAt;
});

const canLoadComponents = computed(() => {
  return !props.hasError && !props.loading;
});

const handleHomeCashError = (isError: boolean) => {
  hasHomeCashError.value = isError;
};
</script>

<style lang="less" scoped>
@max-width: 1008px;

.home-balance-summary {
  align-items: flex-start;

  &__total {
    max-width: @max-width;
    margin: auto;
  }

  &-error {
    max-width: @max-width;
    margin: @size-spacing-x1000 auto;
    display: block;
  }

  &--loaded {
    display: block;
  }

  &-loader {
    max-width: @max-width;
    margin: @size-spacing-x1000 auto;
    display: block;
  }

  .home-balance-container {
    .to-md({
      overflow: auto;
      width: 100%;
    });
    &-error {
      max-width: @max-width;
      margin: @size-spacing-x1000 auto;
      display: block;
    }
  }

  :deep(.base-text),
  :deep(.heading) {
    color: @text-over-image !important;
  }
}
.home-view__cash {
  margin-top: @size-spacing-x1000;
}
.home-view__hero {
  margin-top: @size-spacing-x1000;
}
</style>
